/* lib/unsavedGuard.js — ядро единого сторожа несохранённого (kitchen-ux A2: K-01, K-02, K-27).
   Без React и без window — провайдер (app/providers/UnsavedChangesProvider.jsx) даёт ему
   диалог и историю браузера, тесты — фейки.

   Реестр: ключ формы (у редакторов «Продукции» — уровень стека `type-id`) →
     { dirty, save, label, aliveAt }
     save()      — сохранить форму; true/undefined — успех, false/исключение — не вышло
                   (форма сама показала ошибку у поля);
     label       — как назвать форму в диалоге («ТТК «Борщ»»);
     aliveAt(loc) — смонтирована ли форма по адресу loc (для «назад» браузера).
   runGuard — диалог «Сохранить / Отбросить / Отмена» перед переходом.
   createPopGuard — «назад»/жест при грязной форме: вернуть адрес и спросить то же. */

export const CHOICE = { SAVE: 'save', DISCARD: 'discard', CANCEL: 'cancel' };

// saving — идёт своё сохранение формы (кнопка «Сохранить» в редакторе): диалог сторожа на это
// время выключает кнопки (круг 3, T3bL — «Отбросить» посреди записи давал частичную запись).
// subscribe(fn) — сообщать об изменениях (диалог перерисовывается по ходу записи).
export function createUnsavedRegistry() {
  const map = new Map();
  const listeners = new Set();
  const changed = () => { listeners.forEach((fn) => { try { fn(); } catch (e) { /* noop */ } }); };
  return {
    set(key, patch) {
      const cur = map.get(key) || { dirty: false, save: null, label: null, aliveAt: null, saving: false };
      map.set(key, { ...cur, ...patch });
      changed();
    },
    remove(key) { map.delete(key); changed(); },
    markClean(key) {
      const cur = map.get(key);
      if (cur) { map.set(key, { ...cur, dirty: false }); changed(); }
    },
    // вернуть грязь: «Отбросить» согласовали, а переход не состоялся — форма по-прежнему правлена
    markDirty(key) {
      const cur = map.get(key);
      if (cur) { map.set(key, { ...cur, dirty: true }); changed(); }
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    entry(key) { return map.get(key) || null; },
    dirtyKeys(keys) {
      const out = [];
      map.forEach((e, k) => { if (e.dirty && (!keys || keys.includes(k))) out.push(k); });
      return out;
    },
  };
}

// reason — почему переход (например, «в другой вкладке выбрано пространство …»); диалог
// показывает его над списком несохранённого.
// action → false (или исключение) — переход не состоялся: после «Отбросить» грязь форм
// возвращается (иначе следующий уход молча потерял бы правку — T9 верификатора).
// onBusy('save') — идёт запись из диалога, onBusy('leave') — идёт сам переход, onBusy(false) — всё.
export async function runGuard({ registry, keys, ask, action, onBusy, reason }) {
  const dirty = registry.dirtyKeys(keys);
  if (!dirty.length) { const done = await action(); return done !== false; }
  const entries = dirty.map((k) => registry.entry(k));
  const canSave = entries.every((e) => e && typeof e.save === 'function');
  const labels = entries.map((e) => e && e.label).filter(Boolean);
  const choice = await ask(reason ? { keys: dirty, labels, canSave, reason } : { keys: dirty, labels, canSave });
  const busy = (b) => { if (onBusy) onBusy(b); };
  if (choice === CHOICE.SAVE && canSave) {
    try {
      busy('save');
      for (const k of dirty) {
        const e = registry.entry(k);
        if (!e || !e.dirty) continue;                 // сохранилась сама, пока ждали ответа, — второй записи нет
        let ok = false;
        try { ok = (await e.save()) !== false; } catch (err) { ok = false; }
        if (!ok) return false;
        registry.markClean(k);
      }
      busy('leave');
      return (await action()) !== false;
    } finally {
      busy(false);
    }
  }
  if (choice === CHOICE.DISCARD) {
    const discarded = dirty.filter((k) => { const e = registry.entry(k); return e && e.dirty; });
    discarded.forEach((k) => registry.markClean(k));
    const restore = () => discarded.forEach((k) => registry.markDirty(k));
    try {
      busy('leave');
      let done;
      try { done = await action(); } catch (err) { restore(); throw err; }
      if (done === false) { restore(); return false; }
      return true;
    } finally {
      busy(false);
    }
  }
  return false;
}

// Грязные формы, живые по адресу from и не живые по адресу to — их потеряет переход.
// Без aliveAt форма живёт, пока не сменился путь.
export function keysLostOnNavigation(registry, from, to) {
  return registry.dirtyKeys().filter((k) => {
    const e = registry.entry(k);
    const alive = typeof e.aliveAt === 'function'
      ? e.aliveAt
      : (loc) => !!loc && !!from && loc.pathname === from.pathname;
    return alive(from) && !alive(to);
  });
}

/* Обработчик popstate — роутер зовёт его ДО синхронизации адреса (RR.setPopstateGuard).
   Браузер к этому моменту уже встал на другую запись. Грязная форма потеряется →
   ОТКАТИТЬ переход (history.go на разницу индексов записей, роутер пишет их в
   history.state.__rrIdx) и открыть диалог. Перезаписывать адрес (replaceState) нельзя:
   так затиралась соседняя запись — «назад» потом уводил не туда, «вперёд» залипал.
   · свой откат приходит popstate'ом — его проглатываем (expectIdx);
   · «Сохранить/Отбросить» — переход туда, куда просил браузер (последний перехват), и этот
     popstate отдаём роутеру (bypassIdx);
   · диалог уже открыт — новое «назад» тоже откатывается, второго диалога нет.
   Записи без индекса (pushState не через роутер) — запасной путь: вернуть адрес на месте.

   Ответ раньше отката (V2r): пока наш откат в пути, второй history.go браузер не выполняет
   (Chromium его сбрасывает; другой браузер мог бы отменить откат) — поэтому переход после
   «Сохранить/Отбросить» ждёт popstate отката и выполняется сразу после него. Пришёл НЕ наш
   откат (браузер отменил его чужим переходом) — не ждём его больше, отложенный переход
   забываем. Согласованный переход не состоялся (пришло другое событие) — обход протух. */
export function createPopGuard({ getFrom, readTo, lostKeys, isPending, startGuard, go, restoreFallback, navigateFallback }) {
  let expectIdx = null;   // ждём popstate своего отката на эту запись
  let bypassIdx = null;   // popstate на эту запись — согласованный переход, отдать роутеру
  let target = null;      // последний перехваченный переход {from, to, indexed}
  let deferred = null;    // ответ пришёл раньше отката — переход после его popstate
  const leave = (t) => { bypassIdx = t.to.idx; go(t.to.idx - t.from.idx); };
  return () => {
    const to = readTo();
    if (!to) return false;
    if (expectIdx != null) {
      if (to.idx === expectIdx) {
        expectIdx = null;
        const run = deferred;
        deferred = null;
        if (run) run();
        return true;
      }
      expectIdx = null;
      deferred = null;
    }
    if (bypassIdx != null) {
      if (to.idx === bypassIdx) { bypassIdx = null; return false; }
      bypassIdx = null;
    }
    const from = getFrom();
    if (!from) return false;
    if (from.pathname === to.pathname && (from.search || '') === (to.search || '')) return false;
    const keys = lostKeys(from, to);
    if (!keys.length) return false;
    const indexed = from.idx != null && to.idx != null && from.idx !== to.idx;
    if (indexed) { expectIdx = from.idx; go(from.idx - to.idx); } else restoreFallback(from);
    target = { from, to, indexed };
    if (!isPending()) {
      startGuard(keys, () => {
        const t = target;
        target = null;
        if (!t) return;
        if (!t.indexed) { navigateFallback(t.to); return; }
        if (expectIdx != null) deferred = () => leave(t);
        else leave(t);
      });
    }
    return true;
  };
}

/* Сторож navigate() — роутер зовёт его до записи перехода (RR.setNavigateGuard): палитра
   Ctrl+K, «Редактировать в чате», ссылки уведомлений, редиректы — любой уход с грязной
   формы. Потеряется форма → диалог; proceed выполнит переход после «Сохранить/Отбросить».
   Пока идёт действие самого сторожа (переход, на который пользователь уже согласился),
   не вмешиваемся. Диалог уже открыт — этот переход отбрасываем. */
export function createNavigateGuard({ lostKeys, isPending, isBypassed, startGuard }) {
  return (from, to, proceed) => {
    if (isBypassed()) return false;
    const keys = lostKeys(from, to);
    if (!keys.length) return false;
    if (!isPending()) startGuard(keys, proceed);
    return true;
  };
}

/* Контроллер диалога: один вопрос за раз. Второй guard, пока диалог открыт, отклоняется
   (false) без вопроса; действие, на которое пользователь согласился, выполняется «в обход»
   сторожа navigate() (isBypassed) — иначе собственный переход спросил бы ещё раз. */
export function createGuardController({ registry, ask, onBusy, onSettled }) {
  let pending = false;
  let bypass = 0;
  const guard = async (action, opts = {}) => {
    // результат действия — наружу: false = переход не состоялся (runGuard после «Отбросить»
    // возвращает формам грязь; круг 4 — раньше обёртка его глотала)
    const run = async () => {
      bypass += 1;
      try { return await action(); } finally { bypass -= 1; }
    };
    if (!registry.dirtyKeys(opts.keys).length) { const done = await run(); return done !== false; }
    if (pending) return false;
    pending = true;
    try {
      return await runGuard({ registry, keys: opts.keys, ask, action: run, onBusy, reason: opts.reason });
    } finally {
      pending = false;
      if (onSettled) onSettled();
    }
  };
  return { guard, isPending: () => pending, isBypassed: () => bypass > 0 };
}

/* Установить оба сторожа в роутер. win — окно (в тестах поддельное), router — RR,
   getAppLocation — где стоит приложение ({pathname, search, state, idx}). → снять. */
export function installHistoryGuards({ win, router, registry, controller, getAppLocation, navigateTo }) {
  const lostKeys = (from, to) => keysLostOnNavigation(registry, from, to);
  const readTo = () => {
    const st = win.history.state;
    return {
      pathname: win.location.pathname || '/',
      search: win.location.search || '',
      state: (st && st.__rr) ?? null,
      idx: st && typeof st.__rrIdx === 'number' ? st.__rrIdx : null,
    };
  };
  const startGuard = (keys, proceed) => { controller.guard(proceed, { keys }); };
  const onPop = createPopGuard({
    getFrom: getAppLocation,
    readTo,
    lostKeys,
    isPending: controller.isPending,
    startGuard,
    go: (delta) => win.history.go(delta),
    // пространство записи (__ws, lib/router.jsx) — вкладки: возвращаем адрес, на котором стоит приложение
    restoreFallback: (from) => win.history.replaceState({
      __rr: from.state ?? null, __rrIdx: from.idx,
      ...(router && typeof router.currentHistoryWs === 'function' ? { __ws: router.currentHistoryWs() } : {}),
    }, '', from.pathname + from.search),
    navigateFallback: (to) => navigateTo(to),
  });
  const onNavigate = createNavigateGuard({ lostKeys, isPending: controller.isPending, isBypassed: controller.isBypassed, startGuard });
  const offPop = router.setPopstateGuard(onPop);
  const offNav = router.setNavigateGuard(onNavigate);
  return () => { offPop(); offNav(); };
}
